import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import SEO from "@/components/base/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import { getArticleBySlug, getRelatedArticles, type Article } from "@/mocks/articles";
import NotFound from "@/pages/NotFound";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (slug) {
      const found = getArticleBySlug(slug);
      if (found) {
        setArticle(found);
        setRelatedArticles(getRelatedArticles(found, 4));
        setNotFound(false);
      } else {
        setNotFound(true);
      }
    }
  }, [slug]);

  if (notFound) return <NotFound />;
  if (!article) return null;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.summary,
    author: {
      "@type": "Organization",
      name: article.author,
    },
    datePublished: article.publishedAt,
    publisher: {
      "@type": "Organization",
      name: "51黑料网",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.51heiliaowang.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <SEO
        title={`${article.title} - 51黑料网`}
        description={article.summary}
        keywords={`51黑料网,${article.categoryName},${article.tags.join(",")}`}
        canonicalUrl={`https://www.51heiliaowang.com/article/${article.slug}`}
        ogType="article"
      />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <article>
          <div className="bg-background-100 border-b border-background-200/60">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  { label: article.categoryName, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-2.5 py-1 rounded-full text-xs bg-primary-100 text-primary-700">
                  {article.categoryName}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishedAt}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line"></i>
                  {article.comments} 评论
                </span>
              </div>
            </header>

            <div className="mb-8 rounded-lg overflow-hidden bg-background-100">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
                title={article.title}
              />
            </div>

            <div
              className="prose prose-base max-w-none text-foreground-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <style>{`
              .article-content p {
                margin-bottom: 1.25rem;
                font-size: 1rem;
                line-height: 1.85;
                color: oklch(var(--foreground-800));
              }
            `}</style>

            <div className="mt-10 pt-6 border-t border-background-200/60">
              <p className="text-xs text-foreground-500">
                免责声明：本文内容来源于网络公开信息，仅供读者参考。51黑料网对文中观点保持中立，不对其真实性、完整性、准确性做任何保证。如涉及侵权，请及时联系我们处理。
              </p>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedArticles.map((item) => (
                  <ArticleCard key={item.id} article={item} variant="default" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}